import React, { createContext, useContext, useEffect, useState } from 'react';
import 'core-js/actual/array/flat-map';
import * as PropTypes from 'prop-types';
import useStateLocalPersist from 'src/hooks/use-state-local-persist';
import { setOnboardingCustomerType } from '../helpers/tealium-events';
import MiniAppEvents from 'src/helpers/miniapp-events';
import { Environment } from 'src/Environment';
import { QueryConfigContext } from './query-config-context';
import { Channel, CHANNEL_KEY } from 'src/containers/onboarding/helpers/channel';
import useTechnicalErrorHandler from 'src/hooks/useTechnicalError';
import { BUSINESS_TYPE_KEY_TO_NAME_LOOKUP } from 'src/helpers/constants';
import { getAcquiringChannels } from 'src/containers/onboarding/helpers/acquiring-channel';
import { checkAcquiringChannel } from 'src/helpers/devices';
import { getDocumentTypeCaseInsensitive } from 'src/helpers/functional-helpers';
import { toLowerCaseIfTruthy } from 'src/helpers/formatting';
import { getAllDeviceIdsByName } from '../helpers/devices';
import { BANK_ACCOUNT_BAV_STATUS, BANK_ACCOUNT_HOLDER_TYPE } from '../services/salesforce.service';
import { reloadEnvironmentConfig } from 'src/Environment';
import { isEqualCaseInsensitive } from 'src/helpers/validation';
import { PendingAppService } from 'src/services/pending-apps-service';
import { StepNames } from 'src/features/common/helpers/dto-mappers/salesforce/step-names';
import { AcquiringChannel } from 'src/features/common';
import { VALIDATION_QUINTICA_FILE } from 'src/features/common/services/salesforce-service';
import { LegacyImageService } from 'src/services/image-join-legacy';
import { DEVICE_NAMES } from 'src/features/order/tech-debt/product-details-extra';

export const Steps = {
  LETS_GET_STARTED: "Let's get started",
  SELECT_PRODUCT: 'Select product',
  BUSINESS_TYPE: 'Business Type',
  MONTHLY_CARD_TURNOVER: 'Monthly Card Turnover',
  CHOOSE_A_SOLUTION: 'Choose a Solution',
  CHOOSE_ECOMMERCE_PRODUCT: 'Tender Type',
  YOUR_BUSINESS: 'Your Business',
  BUSINESS_DETAILS: 'Business Details',
  BUSINESS_ADDRESS: 'Business Address',
  BUSINESS_IDS: 'Business Ids',
  YOUR_DETAILS: 'Your Details',
  MORE_DETAILS: 'More Details',
  YOUR_DETAILS_SELFIE: 'Selfie',
  BANKING_DETAILS: 'Banking Details',
  UPLOAD_DOCUMENTS: 'Upload documents',
  CONFIRM_APPLICATION: 'Confirm Application',
  CONFIRM_APPLICATION_PAYMENTS: 'Payments',
  DIRECTOR_ADDRESS: 'Director Address',
  CUSTOMER_ADDRESS: 'Customer Address',
  PENDING_APPS: 'Pending Apps',
};

export const CustomerTypes = {
  dealer: 'dealer',
  agent: 'agent',
  normal: 'normal',
  pos: 'pos',
};

export const FicaStatus = {
  nonStarted: 'nonStarted',
  inProgress: 'inProgress',
  successful: 'successful',
  unsuccessful: 'unsuccessful',
  pending: 'pending',
};

const keys = {
  authTokenKey: 'salesforce_access_token',
  instanceUrlKey: 'salesforce_instance_url',
  leadIdKey: 'lead_id',
  leadKey: 'lead',
  merchantKey: 'merchant',
  merchantSavedKey: 'merchant_saved',
  documentsKey: 'documents',
  documentsCacheKey: 'assets/documents',
  terminalsKey: 'terminals',
  priceTierKey: 'priceTier',
  otpReferenceNumberKey: 'otp_reference_number',
  otpEmailAddressKey: 'otp_email_address',
  isResumedSessionKey: 'is_resumed_session',
  deviceDeliveryDetailsKey: 'device_delivery_details',
  opportunityCardFeesKey: 'opportunity_card_fees',
  cacheName: 'vod-pay-onboarding',
  solutionType: 'solution_type',
  customerType: 'customer_type',
  ficaPollingStartTime: 'fica_polling_start_time',
  otpRetriesExceeded: 'otp_retries_exceeded',
  channelJourneyType: 'channel_journey_type',
  lockedFields: 'locked_fields',
};

export const AcquiringChannelFlags = {
  buyOnlineFicaOnline: 'Bought Online – FICA Online',
  buyInstoreFicaOnline: 'Bought Instore – FICA Online',
  rentOnlineFicaOnline: 'Rented Online – FICA Online',
  zeroRentOnlineFicaOnline: 'Zero Rental Online – FICA Online',
  buyInstoreFicaInstore: 'Bought Instore – FICA Instore',
  vodapay: 'VodaPay BizWallet',
  vodacomBusiness: 'Vodacom Business',
  noAcquiringChannelSet: 'No Acquiring Channel set',
};

export const EmailJourneyType = {
  buyInStoreFicaOnline: 'Buy In store - FICA Online',
  buyOrRentOnlineFicaOnline: 'Buy/Rent Online - FICA Online',
};

export const WalletTypes = {
  SOV: 'Biz Wallet',
  BANK_ACCOUNT: 'Bank Account',
};

export const JourneyType = {
  buyInStoreFicaOnline: 'Buy In store - FICA Online',
  buyOrRentOnlineFicaOnline: 'Buy/Rent Online - FICA Online',
  vodacomBusiness: 'Vodacom business',
};

export const LockedFieldNames = {
  ACCOUNT_BUSINESS_TYPE: 'account.business_type',
  ACCOUNT_PROVINCE: 'account.province',
  ACCOUNT_REGION: 'account.region',
  ACCOUNT_STREET: 'account.street',
  ACCOUNT_SUBURB: 'account.suburb',
  ACCOUNT_TRADING_AS: 'account.trading_as',
  ACCOUNT_ZIP_CODE: 'account.zip_code',
  CONTACT_EMAIL: 'contact.email',
  CONTACT_FIRST_NAMES: 'contact.first_names',
  CONTACT_PHONE: 'contact.phone',
  CONTACT_ID_NUMBER: 'contact.id_number',
  LEAD_AGENT_ID: 'lead.agentId',
  LEAD_EMAIL: 'lead.email',
  LEAD_FIRST_NAMES: 'lead.first_names',
  LEAD_MOBILE_NUMBER: 'lead.mobile_number',
  LEAD_MONTHLY_CARD_TURNOVER: 'lead.monthly_card_turnover',
  LEAD_TYPE_OF_BUSINESS: 'lead.type_of_business',
  LEAD_SOURCE_SYSTEM: 'lead.Source_System',
  OPPORTUNITY_DELIVERIES_DEVICE_NAME: 'opportunity.deliveries.device_name',
  OPPORTUNITY_DELIVERIES_NUMBER_OF_DEVICES: 'opportunity.deliveries.number_of_devices',
};

const MAX_SF_CALL_LENGTH = 6000000;
const MAX_FICA_POLLING_DURATION_SEC = 60 * 10;

export const BizWalletName = 'Biz Wallet Pro';
export const kwikaName = 'VodaPay Kwika';
export const maxName = 'VodaPay Max';
export const chopChopName = 'VodaPay Chop-Chop';
export const ecommerceProductCategoryName = 'E commerce Gateway';
export const paymentRequestProductCategoryName = 'Payment Request';

export const SalesforceContext = createContext();

export function SalesforceProvider({ salesforceService, children }) {
  const [authToken, setAuthToken] = useState(null);
  const [instanceUrl, setInstanceUrl] = useState(null);
  const [leadId, setLeadId] = useState(null);
  const [merchant, setMerchant] = useState({});
  const [otpReferenceNumber, setOtpReferenceNumber] = useStateLocalPersist(
    keys.otpReferenceNumberKey,
    null,
  );

  const [otpEmailAddress, setOtpEmailAddress] = useStateLocalPersist(keys.otpEmailAddressKey, null);
  const [isResumedSession, setIsResumedSession] = useState(false);
  const [opportunityCardFees, setOpportunityCardFees] = useState(null);
  const [cache, setCache] = useState(null);
  const [solutionType, setSolutionType] = useState([]);
  const [journeyType, setJourneyType] = useStateLocalPersist(keys.channelJourneyType, null);
  const [isLoadingFromSessionStorage, setIsLoadingFromSessionStorage] = useState(true);
  const [hasBuyOnlineDevice, setHasBuyOnlineDevice] = useState(false);
  const [paymentGatewayAmount, setPaymentGatewayAmount] = useState(0);
  const [customerType, setCustomerType] = useState(CustomerTypes.normal);
  const [dealerId, setDealerId] = useState('');
  const [ficaPollingStartTime, setFicaPollingStartTime] = useState(null);
  const [applicationComplete, setApplicationComplete] = useState(false);
  const [isApplicationKilled, setApplicationKilled] = useState(false);
  const [otpRetriesExceeded, setOtpRetriesExceeded] = useStateLocalPersist(
    keys.otpRetriesExceeded,
    false,
  );
  const [, setBankingDetailsPassBav, bankingDetailsPassBavRef] = useStateLocalPersist(
    'bankingDetailsPassBav',
    null,
  );

  const isRented = checkAcquiringChannel(
    AcquiringChannelFlags.rentOnlineFicaOnline,
    merchant?.opportunity?.deliveries,
  );

  const isCreditVettingEnabled = !Environment.current?.disabledFeatures?.creditVetting && isRented;

  const getBankingDetailsPassBav = () => bankingDetailsPassBavRef.current;

  const [merchantSaved, setMerchantSaved] = useStateLocalPersist(keys.merchantSavedKey, null);

  const [lockedFields, setLockedFields] = useStateLocalPersist(keys.lockedFields, []);
  const isFieldLocked = (fieldName) => lockedFields.includes(fieldName);

  const [isIncompleteSOVJourney, setIsIncompleteSOVJourney] = useState(false);
  const handleTechnicalError = useTechnicalErrorHandler();
  const isSovAvailable = () => solutionType.indexOf(AcquiringChannelFlags.vodapay) > -1;
  const getSettlementsMethod = () =>
    merchant?.account?.settlements_method || WalletTypes.BANK_ACCOUNT;

  const { queryConfig } = useContext(QueryConfigContext);

  const isVodapayChannel = queryConfig[CHANNEL_KEY] === Channel.VODAPAY;

  const updateOpportunityCardFees = (opportunityCardFees) => {
    setOpportunityCardFees(opportunityCardFees);
    sessionStorage.setItem(keys.opportunityCardFeesKey, JSON.stringify(opportunityCardFees));
  };

  const isLeadConverted = () =>
    merchant?.contact?.id && merchant?.account?.id && merchant?.opportunity?.id;

  const [leadUnconverted, setLeadUnconverted] = useState({
    firstNames: '',
    mobileNumber: '',
    email: '',
    typeOfBusiness: BUSINESS_TYPE_KEY_TO_NAME_LOOKUP.sole_prop,
  });

  const isVodacomBusinessChannel =
    queryConfig[CHANNEL_KEY] === Channel.VODACOM_BUSINESS ||
    leadUnconverted.solutionType === JourneyType.vodacomBusiness;

  const lead = {
    get value() {
      // after lead is converted i.e. after annual
      // turnover entered, stop using the OnboardingLead endpoint
      // and start using the Merchant endpoint for lead details
      if (!isLeadConverted()) {
        return leadUnconverted;
      }

      return {
        firstNames: merchant?.contact?.first_names,
        mobileNumber: merchant?.contact?.phone || leadUnconverted?.mobileNumber,
        email: merchant?.contact?.email,
        typeOfBusiness:
          merchant?.account?.business_type || BUSINESS_TYPE_KEY_TO_NAME_LOOKUP.sole_prop,
        monthlyCardTurnover: leadUnconverted?.monthlyCardTurnover,
        dealerId: leadUnconverted?.dealerId || '',
        agentId: leadUnconverted?.agentId || '',
        consumerType: leadUnconverted?.consumerType || '',
      };
    },
  }.value;

  const setLead = (lead, updatedMerchant) => {
    if (!isLeadConverted() && !updatedMerchant) {
      setLeadUnconverted(lead);
      return;
    }

    updatedMerchant = {
      ...merchant,
      contact: {
        ...merchant.contact,
        first_names: lead.firstNames,
        phone: lead.mobileNumber,
        email: lead.email,
      },
      account: {
        ...merchant.account,
        business_type: lead.typeOfBusiness || BUSINESS_TYPE_KEY_TO_NAME_LOOKUP.sole_prop,
      },
      opp_resolved: isPendingAppComplete(merchant.substep_name),
      opp_kill_flag: lead.isApplicationKilled,
      ...updatedMerchant,
    };

    setMerchant(updatedMerchant);

    return updatedMerchant;
  };

  const [documents, setDocuments] = useState({});
  const [deletedDocs, setDeletedDocs] = useState([]);
  const [terminals, setTerminals] = useState({});
  const [uniqueTerminals, setUniqueTerminals] = useState({});
  const [deviceDeliveryDetails, setDeviceDeliveryDetails] = useState([]);
  const [priceTier, setPriceTier] = useState(null);
  const [deviceQuantitiesWithAddresses, setDeviceQuantitiesWithAddresses] = useState(null);
  const [isLeadConvertedAsync, setIsLeadConvertedAsync] = useState(false);

  useEffect(() => {
    setIsLeadConvertedAsync(
      merchant?.contact?.id && merchant?.account?.id && merchant?.opportunity?.id,
    );
  }, [merchant?.contact?.id, merchant?.account?.id, merchant?.opportunity?.id]);

  const setDocumentsWithDeletionHandling = (documentsNew) =>
    setDocuments((documentsExisting) => {
      const documentsNewMap = (documentsNew.documents || []).reduce((accum, document) => {
        accum[document.content_document_id] = document;
        return accum;
      }, {});

      const documentsToDelete = (documentsExisting.documents || [])
        .filter((document) => !documentsNewMap[document.content_document_id])
        .map((document) => document.content_document_id);
      setDeletedDocs(documentsToDelete);

      return documentsNew;
    });

  const getUniqueTerminals = (terminals) => {
    const devicesSplitByChannel = Object.values(terminals).reduce((accum, terminal) => {
      const acquiringChannel = terminal.acquiring_channel;

      const termList = acquiringChannel
        .split(';')
        .filter((acq) => acq !== AcquiringChannel.thirdPartyChannel)
        .reduce((accum, acq) => {
          return accum.concat({
            ...terminal,
            acquiring_channel: acq,
            id: terminal.id + acq,
            deviceIdSalesforce: terminal.id,
          });
        }, []);
      return accum.concat(...termList);
    }, []);
    return devicesSplitByChannel.reduce((map, item) => {
      map[item.id] = item;
      return map;
    }, {});
  };

  const initializeTerminals = (terminalsCurrent) => {
    setTerminals(terminalsCurrent);
    setUniqueTerminals(getUniqueTerminals(terminalsCurrent));
  };

  useEffect(() => {
    if (merchant?.opportunity?.deliveries) {
      setHasBuyOnlineDevice(
        Object.values(merchant.opportunity.deliveries).some(
          (delivery) =>
            delivery.acquiring_channel === AcquiringChannelFlags.buyOnlineFicaOnline &&
            delivery.number_of_devices > 0,
        ),
      );

      const sovRequested = Object.values(merchant.opportunity.deliveries).some(
        (delivery) => delivery.device_name === BizWalletName && delivery.number_of_devices > 0,
      );

      if (merchant.account.sov_requested !== sovRequested) {
        setMerchant((merchantCurrent) => ({
          ...merchantCurrent,
          account: {
            ...merchantCurrent.account,
            sov_requested: sovRequested,
          },
        }));
      }
    }
  }, [merchant]);

  useEffect(() => {
    setDeviceQuantitiesWithAddresses(
      deviceDeliveryDetails.reduce((result, deviceOrderInfo) => {
        Object.keys(deviceOrderInfo.devices).forEach((deviceId) => {
          result[deviceId] = (result[deviceId] || 0) + deviceOrderInfo.devices[deviceId];
        });
        return result;
      }, {}),
    );
  }, [deviceDeliveryDetails]);

  useEffect(() => {
    if (cache !== null) {
      getFromCache((value) => setDocuments(value), keys.documentsCacheKey);
    }
  }, [cache]);

  useEffect(() => {
    setOnboardingCustomerType(customerType);
  }, [customerType]);

  const initialiseCacheIfNonExistent = async () => {
    const cacheAvailable = 'caches' in window.self;
    if (cacheAvailable) {
      setCache(await caches.open(keys.cacheName));
    }
  };

  const noBuyInStoreDevicesExist = (terminals) =>
    !Object.values(terminals).some((term) =>
      term.acquiring_channel.split(';').includes(AcquiringChannelFlags.buyInstoreFicaOnline),
    );

  const isHideBuyOnlineDevices =
    MiniAppEvents.isInMiniApp() && !!Environment.current?.disabledFeatures?.miniAppBuyOnlineJourney;

  const addToCache = (req, res) => {
    const options = {
      headers: {
        'Content-Type': 'application/json',
      },
    };
    if (cache !== null) {
      cache.put(req, new Response(res, options));
    }
  };

  const getFromCache = async (loader, req) => {
    if (cache !== null) {
      const response = await cache.match(req);
      if (response !== undefined) {
        const responsePromise = response.json();
        responsePromise.then((res) => loader(res));
      }
    }
  };

  const removeFromCache = async (req) => {
    if (cache !== null) {
      cache.delete(req);
    }
  };

  const clearCache = () => {
    const cacheAvailable = 'caches' in window.self;
    if (cacheAvailable) {
      caches.delete(keys.cacheName);
    }
  };

  const getTotalDevices = () =>
    Object.values(merchant?.opportunity?.deliveries || {})
      .map((delivery) => delivery.number_of_devices)
      .reduce((a, b) => a + b, 0);

  const getDeviceDeliveryQuantity = (deviceId) => {
    return deviceQuantitiesWithAddresses[deviceId] || 0;
  };

  const getKwikaRetailFee = (term) =>
    Object.values(term || terminals).reduce((accum, terminal) => {
      return terminal.name === kwikaName ? terminal.retail_fee_vat_excl : accum;
    }, 0);

  /* This function ensures
  if there are more devices of type deliveryId specified with
  delivery addresses in deviceDeliveryDetails than the total
  quantity in merchant.opportunity.deliveries, device
  quantities are removed from deviceDeliveryDetails
  until the total ordered equal total delivered once
  again. This process starts from the most recently
  added delivery address */
  const ensureDeliveryQuantityMatchesOrderQuantity = (deviceId, deviceOrderQuantity) => {
    const deviceDeliveryQuantity = getDeviceDeliveryQuantity(deviceId);

    let quantityToReduceLeft = deviceDeliveryQuantity - deviceOrderQuantity;

    if (quantityToReduceLeft === 0) {
      return;
    }

    const newDeviceDeliveryDetails = deviceDeliveryDetails
      .reverse()
      .map((deliveryDetail) => {
        const mappedDeviceQuantities = Object.keys(deliveryDetail.devices).reduce(
          (accum, deviceIdCurrent) => {
            if (quantityToReduceLeft > 0 && deviceIdCurrent === deviceId) {
              const reduceQuantity = Math.min(
                quantityToReduceLeft,
                deliveryDetail.devices[deviceIdCurrent],
              );
              quantityToReduceLeft = quantityToReduceLeft - reduceQuantity;
              const newQuantity = accum[deviceId] - reduceQuantity;
              if (newQuantity > 0) {
                accum[deviceId] = accum[deviceId] - reduceQuantity;
              } else {
                delete accum[deviceId];
              }
            }
            return accum;
          },
          { ...deliveryDetail.devices },
        );

        return {
          ...deliveryDetail,
          devices: mappedDeviceQuantities,
        };
      })
      .filter((deliveryDetail) => {
        const totalNumberOfDevices = Object.values(deliveryDetail.devices).reduce(
          (quantityA, quantityB) => quantityA + quantityB,
          0,
        );
        return totalNumberOfDevices > 0;
      })
      .reverse();

    setDeviceDeliveryDetails(newDeviceDeliveryDetails);
  };

  const enrichMerchantWithOpportunityCardFees = (merchant, opportunityCardFees) => ({
    ...merchant,
    opportunity: {
      ...merchant.opportunity,
      card_fees: opportunityCardFees,
    },
  });

  const enrichMerchantWithDeliveryDetails = (merchant, deviceDeliveryDetails) => {
    const deviceQuantities = merchant.opportunity.deliveries.reduce((result, delivery) => {
      result[delivery.device_id] = {
        ...delivery,
        number_of_devices: parseInt(delivery.number_of_devices),
      };
      return result;
    }, {});

    const deviceQuantitiesWithoutAddressMap = deviceDeliveryDetails.reduce(
      (result, deviceOrderInfo) => {
        Object.keys(deviceOrderInfo.devices).forEach((deviceId) => {
          result[deviceId] = {
            ...result[deviceId],
            number_of_devices:
              (result[deviceId] ? result[deviceId].number_of_devices : 0) -
              deviceOrderInfo.devices[deviceId],
          };
        });
        return result;
      },
      deviceQuantities,
    );

    const deviceQuantitiesWithoutAddress = Object.values(deviceQuantitiesWithoutAddressMap)
      .filter((delivery) => delivery.number_of_devices > 0)
      .map((delivery) => {
        const { deviceIdSalesforce, ...cleanedDelivery } = delivery;
        return {
          ...cleanedDelivery,
          device_id: deviceIdSalesforce,
          price: delivery.number_of_devices * delivery.price,
          product_category: delivery.product_category,
          is_product_category: delivery.is_product_category,
          address_type: 'business',
          manager_name: merchant?.contact?.first_names || lead?.firstNames,
          phone: merchant?.contact?.phone || lead?.mobileNumber,
          phone_work: merchant?.contact?.phone,
          phone_cell: lead?.mobileNumber,
          street: merchant?.account?.street,
          suburb: merchant?.account?.suburb,
          Complex_Building: merchant?.account?.building,
          city: merchant?.account?.city,
          region: merchant?.account?.region,
          state: merchant?.account?.region,
          zip_code: merchant?.account?.zip_code,
          gps_location: merchant?.account?.gps_location,
          country: 'South Africa',
          id_number: merchant?.contact?.id_number,
          trading_as: merchant?.account?.trading_as,
        };
      });

    const deviceQuantitiesWithAddress = deviceDeliveryDetails.flatMap((delivery) => {
      return Object.keys(delivery.devices).map((deviceId) => {
        const deviceIdSalesforce = uniqueTerminals[deviceId].deviceIdSalesforce;
        return {
          address_type: 'operational',
          acquiring_channel: uniqueTerminals[deviceId].acquiring_channel,
          device_id: deviceIdSalesforce,
          device_name: uniqueTerminals[deviceId].name,
          product_category: uniqueTerminals[deviceId].product_category,
          is_product_category: uniqueTerminals[deviceId].is_product_category,
          number_of_devices: delivery.devices[deviceId],
          price: delivery.devices[deviceId] * deviceQuantities[deviceId].price,
          manager_name: delivery.manager_name,
          phone: delivery.phone_work || delivery.phone_cell,
          phone_work: delivery.phone_work,
          phone_cell: delivery.phone_cell,
          street: delivery.street,
          complex: delivery.building,
          suburb: delivery.suburb,
          city: delivery.city,
          region: delivery.region,
          state: delivery.region,
          country: 'South Africa',
          zip_code: delivery.zip_code,
          gps_location: delivery.gps_location,
          trading_as: delivery.trading_as,
          id_number: delivery.id_number,
        };
      });
    });

    const merchantDeliveries = deviceQuantitiesWithoutAddress.concat(deviceQuantitiesWithAddress);

    return {
      ...merchant,
      opportunity: {
        ...merchant.opportunity,
        deliveries: merchantDeliveries,
      },
    };
  };

  function getLeadRequestObject(lead) {
    if (journeyType) {
      return {
        ...lead,
        solutionType: journeyType,
      };
    } else {
      return lead;
    }
  }

  function getMerchantRequestObject(merchant, stepName, subStepName = null) {
    let merchantEnriched = merchant;

    if (opportunityCardFees) {
      merchantEnriched = enrichMerchantWithOpportunityCardFees(
        merchantEnriched,
        opportunityCardFees,
      );
    }

    const account = {
      ...merchantEnriched.account,
      pricing_tier: priceTier || merchant?.account?.pricing_tier || null,
    };

    const {
      bank_account_holder_business,
      bank_account_holder_personal,
      bank_account_holder,
      ...bankAccount
    } = {
      ...merchantEnriched.bank_account,
    };

    let bank_account_holder_sf = null;

    if (isCreditVettingEnabled) {
      switch (bankAccount.bank_account_holder_type) {
        case BANK_ACCOUNT_HOLDER_TYPE.BUSINESS:
          bank_account_holder_sf = bank_account_holder_business;
          break;

        case BANK_ACCOUNT_HOLDER_TYPE.PERSONAL:
          bank_account_holder_sf = bank_account_holder_personal;
          break;
      }

      const isBavPassed = getBankingDetailsPassBav();
      if (isBavPassed !== null) {
        bankAccount.bav_status = getBankingDetailsPassBav()
          ? BANK_ACCOUNT_BAV_STATUS.VALID
          : BANK_ACCOUNT_BAV_STATUS.INVALID;
      }
    } else {
      bank_account_holder_sf = bank_account_holder;
    }

    merchantEnriched = {
      ...merchantEnriched,
      bank_account: {
        ...bankAccount,
        bank_account_holder: bank_account_holder_sf || bank_account_holder,
      },
      opp_resolved: isPendingAppComplete(subStepName),
      opp_kill_flag: isApplicationKilled,
      opportunity: {
        ...merchantEnriched.opportunity,
        deliveries: Object.values(merchantEnriched.opportunity.deliveries),
        solutionType: journeyType,
      },
      account: account,
      step_name: stepName,
      substep_name: stepName !== StepNames.CONFIRM_APPLICATION ? subStepName : null,
    };

    return enrichMerchantWithDeliveryDetails(merchantEnriched, deviceDeliveryDetails);
  }

  const isConnectionInitialized = () => authToken && instanceUrl;

  const initializeConnection = async () => {
    const connection = await salesforceService.createAuthTokenAndInstanceUrl();
    setAuthToken(connection.access_token);
    setInstanceUrl(connection.instance_url);
    return connection;
  };

  const resumeSessionRequestOTP = async (email, referenceNumber) => {
    let authTokenCurrent = authToken;
    let instanceUrlCurrent = instanceUrl;
    if (!isConnectionInitialized()) {
      const connection = await initializeConnection();
      authTokenCurrent = connection.access_token;
      instanceUrlCurrent = connection.instance_url;
    }

    if (email) {
      setOtpEmailAddress(email);
    }

    if (referenceNumber) {
      setOtpReferenceNumber(referenceNumber);
    }
    const response = await salesforceService.requestOTP(
      instanceUrlCurrent,
      authTokenCurrent,
      referenceNumber || otpReferenceNumber,
      email || otpEmailAddress,
    );

    const error = response.Message === 'OTP created.' ? null : response.Message;

    return {
      error: error,
    };
  };

  const revertApplicatonToSavedState = async () => {
    if (merchantSaved.step_name === Steps.CHOOSE_A_SOLUTION) {
      const merchantOldState = {
        ...merchantSaved,
        opportunity: {
          ...merchantSaved.opportunity,
          deliveries: merchant.opportunity.deliveries,
        },
      };

      await salesforceService.updateMerchant(
        instanceUrl,
        authToken,
        getMerchantRequestObject(merchantOldState, Steps.CHOOSE_A_SOLUTION),
      );
    } else {
      throw new Error(
        'SF Context: revertApplicatonToSavedState only supports the choose solution step',
      );
    }
  };

  const clearApplicationState = async () => {
    sessionStorage.clear();
    localStorage.clear();
    reloadEnvironmentConfig();
  };

  const resumeSessionSubmitOTP = async (otp) => {
    let authTokenCurrent = authToken;
    let instanceUrlCurrent = instanceUrl;
    if (!isConnectionInitialized()) {
      const connection = await initializeConnection();
      authTokenCurrent = connection.access_token;
      instanceUrlCurrent = connection.instance_url;
    }

    const { ResponseData, Message } = await salesforceService.submitOTP(
      instanceUrlCurrent,
      authTokenCurrent,
      otpReferenceNumber,
      otpEmailAddress,
      otp,
    );

    if (Message) {
      return {
        result: null,
        error: Message,
      };
    }

    const response = JSON.parse(ResponseData);
    let currentCustomerType;
    let currentLead = {};
    let currentJourneyType = null;

    if (response) {
      const terminalsCurrent = await salesforceService.getTerminals(
        instanceUrlCurrent,
        authTokenCurrent,
      );
      initializeTerminals(terminalsCurrent);
      sessionStorage.setItem(keys.terminalsKey, JSON.stringify(terminalsCurrent));

      if (response?.lead?.lead) {
        currentJourneyType = response?.lead?.lead?.solutionType || null;

        const consumerType = response.lead.lead.consumer_type;

        currentLead = {
          firstNames: response.lead.lead.first_names,
          mobileNumber: response.lead.lead.mobile_number,
          email: response.lead.lead.email,
          typeOfBusiness:
            response.lead.lead.type_of_business || BUSINESS_TYPE_KEY_TO_NAME_LOOKUP.sole_prop,
          monthlyCardTurnover: response.lead.lead.monthly_card_turnover,
          agentId: response.lead.lead.agentId,
          dealerId: response.lead.lead.footSoldier_ID,
          solutionType: currentJourneyType,
          consumerType: consumerType ? consumerType.toLowerCase() : consumerType,
        };

        if (currentJourneyType === JourneyType.vodacomBusiness) {
          currentLead = {
            ...currentLead,
            sourceSystem: response?.lead?.lead?.Source_System,
            deviceType: response?.lead?.lead?.device_type,
            deviceQuantity: response?.lead?.lead?.device_quantity,
            postalCode: response?.lead?.lead?.zip_code,
            suburb: response?.lead?.lead?.suburb,
            street: response?.lead?.lead?.street,
            province: response?.lead?.lead?.state || response?.lead?.lead?.region,
            tradingAs: response?.lead?.lead?.trading_as,
            idNumber: response?.lead?.lead?.id_number,
            dealerIdVodacomBusiness: response?.lead?.lead?.dealer_id,
          };

          if (response?.locked_fields) {
            setLockedFields(response?.locked_fields.split(','));
          }
        }

        setLead(currentLead);
        sessionStorage.setItem(keys.leadKey, JSON.stringify(currentLead));

        let savedCustomerType = CustomerTypes.normal;
        if (
          toLowerCaseIfTruthy(currentLead?.solutionType) ===
          toLowerCaseIfTruthy(AcquiringChannelFlags.vodacomBusiness)
        ) {
          savedCustomerType = CustomerTypes.pos;
        } else if (currentLead?.agentId) {
          savedCustomerType = CustomerTypes.agent;
        } else if (currentLead?.dealerId) {
          savedCustomerType = CustomerTypes.dealer;
        }
        setCustomerType(savedCustomerType);
        sessionStorage.setItem(keys.customerType, savedCustomerType);
        currentCustomerType = savedCustomerType;

        const acquiringChannels = getAcquiringChannels(
          savedCustomerType,
          currentJourneyType,
          response?.lead?.lead?.type_of_business || BUSINESS_TYPE_KEY_TO_NAME_LOOKUP.sole_prop,
          isHideBuyOnlineDevices,
          noBuyInStoreDevicesExist(terminalsCurrent),
          isVodapayChannel, // TODO: get from OTP response once SF makes change
        );

        setJourneyType(currentJourneyType);
        setSolutionType(acquiringChannels);
        sessionStorage.setItem(keys.solutionType, JSON.stringify(acquiringChannels));

        const leadIdNew = response?.lead?.lead?.id;
        if (leadIdNew) {
          sessionStorage.setItem(keys.leadIdKey, leadIdNew);
          setLeadId(leadIdNew);
        }
      }

      if (response?.documents?.documents) {
        const filteredDocumments = markDocumentsAsSent(response?.documents);
        setDocuments(filteredDocumments);
        removeFromCache(keys.documentsCacheKey); // Does not overwrite so clear before adding
        addToCache(keys.documentsCacheKey, JSON.stringify(filteredDocumments));
      }

      let merchantPayload = {};

      if (response?.merchant) {
        /**
         * @todo Please remove the following lines marked with [ECOM], when SF returns ecommerce gateway correctly in OTP.
         */
        // [ECOM]
        let isEcommerce = false;
        const deliveriesMap = response.merchant.opportunity.deliveries.reduce((accum, element) => {
          // [ECOM] whole if needs to be removed
          if (
            terminalsCurrent[element.device_id].product_category === ecommerceProductCategoryName ||
            terminalsCurrent[element.device_id].product_category ===
              paymentRequestProductCategoryName
          ) {
            isEcommerce = true;
          }
          let acquiringChannel = null;
          acquiringChannel = element.acquiring_channel;

          const newDeviceId = element.device_id + acquiringChannel;
          const price =
            element.number_of_devices && element.price && element.number_of_devices !== 0
              ? element.price / element.number_of_devices
              : 0;
          accum[newDeviceId] = {
            ...element,
            acquiringChannel: acquiringChannel,
            deviceIdSalesforce: element.device_id,
            device_id: newDeviceId,
            price: price,
            product_category: terminalsCurrent[element.device_id].product_category,
            is_product_category: terminalsCurrent[element.device_id].is_product_category,
          };
          return accum;
        }, {});

        // [ECOM] remove this whole if
        if (isEcommerce) {
          const ecommerceCategoryDetails = Object.values(terminalsCurrent).find(
            (terminal) =>
              (terminal.product_category === ecommerceProductCategoryName ||
                terminal.product_category === paymentRequestProductCategoryName) &&
              terminal.is_product_category,
          );
          const newDeviceId =
            ecommerceCategoryDetails.id + ecommerceCategoryDetails.acquiring_channel;

          if (!deliveriesMap[newDeviceId]) {
            deliveriesMap[newDeviceId] = {
              account_id: null,
              acquiringChannel: ecommerceCategoryDetails.acquiring_channel,
              acquiring_channel: ecommerceCategoryDetails.acquiring_channel,
              address_type: 'BUSINESS',
              city: null,
              complex: null,
              country: null,
              deviceIdSalesforce: ecommerceCategoryDetails.id,
              device_id: newDeviceId,
              device_name: ecommerceCategoryDetails.name,
              gps_location: null,
              id_number: null,
              is_product_category: ecommerceCategoryDetails.is_product_category,
              manager_name: null,
              number_of_devices: 1,
              phone: null,
              phone_cell: null,
              phone_work: null,
              price: 0,
              product_category: ecommerceCategoryDetails.product_category,
              region: null,
              state: null,
              street: null,
              suburb: null,
              trading_as: null,
              zip_code: null,
            };
          }
        }

        merchantPayload = {
          ...response.merchant,
          opportunity: {
            ...response.merchant.opportunity,
            deliveries: deliveriesMap,
          },
        };

        if (currentJourneyType === JourneyType.vodacomBusiness) {
          /* scenario: user resumes application via OTP, merchant object already exists in SF
           * ensure correct vodacom business details are set in local state from lead object
           * in OTP response i.e. device, device quantity, postal code, suburb, street and province
           */
          const kwikaVodacomBusinessDevice = Object.values(
            getUniqueTerminals(terminalsCurrent),
          ).find(
            (terminal) =>
              isEqualCaseInsensitive(terminal.name, currentLead.deviceType) &&
              terminal.acquiring_channel === AcquiringChannelFlags.vodacomBusiness,
          );
          if (!kwikaVodacomBusinessDevice) {
            handleTechnicalError(
              `${currentLead.deviceType} device with vodacom business acquiring channel expected but not configured in salesforce`,
            );
          }

          const kwikaQuantity = currentLead.deviceQuantity;
          if (!kwikaQuantity) {
            handleTechnicalError(
              `Quantity for ${currentLead.deviceName} device expected in OTP response`,
            );
          }

          merchantPayload = {
            ...merchantPayload,
            opportunity: {
              ...merchantPayload.opportunity,
              deliveries: {
                [kwikaVodacomBusinessDevice.id]: {
                  price: getKwikaRetailFee(terminalsCurrent),
                  number_of_devices: kwikaQuantity,
                  device_id: kwikaVodacomBusinessDevice.id,
                  device_name: kwikaVodacomBusinessDevice.name,
                  acquiring_channel: kwikaVodacomBusinessDevice.acquiring_channel,
                  deviceIdSalesforce: kwikaVodacomBusinessDevice.deviceIdSalesforce,
                },
              },
            },
            account: {
              ...merchantPayload.account,
              zip_code: merchantPayload.account.zip_code || currentLead.postalCode,
              suburb: merchantPayload.account.suburb || currentLead.suburb,
              street: merchantPayload.account.street || currentLead.street,
              province:
                merchantPayload.account.province ||
                merchantPayload.account.region ||
                currentLead.province,
              region:
                merchantPayload.account.province ||
                merchantPayload.account.region ||
                currentLead.province,
              trading_as: merchantPayload.account.trading_as || currentLead?.tradingAs,
              trading_name_different_to_business_name:
                !!merchantPayload.account.trading_as || !!currentLead?.tradingAs,
            },
            contact: {
              ...merchantPayload.contact,
              id_number: merchantPayload.account.id_number || currentLead.idNumber,
            },
            opp_resolved: merchantPayload.opp_resolved,
            opp_kill_flag: merchantPayload.opp_kill_flag,
          };
        }

        const bankAccount = {
          ...merchantPayload.bank_account,
        };

        if (isCreditVettingEnabled) {
          switch (bankAccount.bank_account_holder_type) {
            case BANK_ACCOUNT_HOLDER_TYPE.BUSINESS:
              bankAccount.bank_account_holder_business = bankAccount.bank_account_holder;
              break;

            case BANK_ACCOUNT_HOLDER_TYPE.PERSONAL:
              bankAccount.bank_account_holder_personal = bankAccount.bank_account_holder;
              break;
          }

          if (bankAccount.bav_status !== null) {
            setBankingDetailsPassBav(
              isEqualCaseInsensitive(bankAccount.bav_status, BANK_ACCOUNT_BAV_STATUS.VALID),
            );
          }
        }

        setMerchant({
          ...merchantPayload,
          bank_account: bankAccount,
        });

        sessionStorage.setItem(keys.merchantKey, JSON.stringify(merchantPayload));

        const acquiringChannels = getAcquiringChannels(
          currentCustomerType,
          currentJourneyType,
          response?.lead?.lead?.type_of_business || BUSINESS_TYPE_KEY_TO_NAME_LOOKUP.sole_prop,
          isHideBuyOnlineDevices,
          noBuyInStoreDevicesExist(terminalsCurrent),
          isVodapayChannel, // TODO: get from OTP response once SF makes change
        );

        setSolutionType(acquiringChannels);
        sessionStorage.setItem(keys.solutionType, JSON.stringify(acquiringChannels));
      }

      setIsResumedSession(true);
      sessionStorage.setItem(keys.isResumedSessionKey, true);

      if (response.responses && response.responses.length > 0) {
        merchantPayload.isPendingApp = true;

        const pageUpdates = await PendingAppService.getUpdateInformation(
          {
            order_ref_number: merchantPayload.opportunity.reference_no,
            journey_name: 'legacy',
            responses: response.responses.map((response) => response.response),
          },
          new AbortController(),
        );

        merchantPayload.pageUpdates = pageUpdates.map((category) => ({
          categoryName: category.category_name,
          stepName: category.stepName,
          subStepName: category.subStepName,
          fields: category.fields.map((field) => ({
            fieldName: field.fieldName,
            hints: field.responses.map((response) => ({
              code: response.responseCode,
              name: response.productName,
              hint: response.uiExplanation,
              id: response.identityNumber,
            })),
          })),
          uiExplanation: category.subStepDesc,
        }));

        const merchantToSave = setLead(currentLead, merchantPayload);
        sessionStorage.setItem(keys.merchantKey, JSON.stringify(merchantToSave));
      }

      if (merchantPayload.isPendingApp && merchantPayload.pageUpdates) {
        return {
          result: {
            stepName: Steps.PENDING_APPS,
            subStepName: null,
            merchant: response?.merchant,
          },
          error: null,
        };
      }

      return {
        result: {
          stepName: response?.merchant?.step_name || response?.lead?.step_name,
          subStepName: response?.merchant?.substep_name,
          merchant: response?.merchant,
        },
        error: null,
      };
    }
  };

  const removeUnsentDocuments = (documents) => {
    return {
      documents: documents.documents.filter(
        (document) => !document.sent && document.document_type !== VALIDATION_QUINTICA_FILE,
      ),
    };
  };

  const markDocumentsAsSent = (documents) => {
    return {
      documents: documents.documents.map((document) => ({ ...document, sent: true })),
    };
  };

  const splitDocumentCalls = (documents) => {
    const totalSize = documents.documents.reduce(
      (acc, curr) => acc + curr.document_body?.length || 0,
      0,
    );
    if (totalSize > MAX_SF_CALL_LENGTH) {
      const documentList = documents.documents.map((doc) => {
        return { documents: [doc] };
      });
      return documentList;
    } else {
      return [documents];
    }
  };

  const getFicaStatus = () => {
    const ficaStatus = merchant?.opportunity?.fica_status || null;

    switch (ficaStatus) {
      case 'Passed':
        return FicaStatus.successful;
      case 'Failed':
        return FicaStatus.unsuccessful;
    }

    if (
      ficaPollingStartTime &&
      (Date.now() - ficaPollingStartTime) / 1000 >= MAX_FICA_POLLING_DURATION_SEC
    ) {
      return FicaStatus.pending;
    }

    return FicaStatus.inProgress;
  };

  const initFicaPollingStartTime = () => {
    if (!ficaPollingStartTime) {
      const now = Date.now();
      setFicaPollingStartTime(() => now);
      sessionStorage.setItem(keys.ficaPollingStartTime, JSON.stringify(now));
    }
  };

  const refreshFicaStatus = async () => {
    const response = await salesforceService.getMerchant(instanceUrl, authToken, leadId);
    const ficaStatus = response.opportunity?.fica_status || null;

    const merchantNewValue = {
      ...merchant,
      opportunity: {
        ...merchant.opportunity,
        fica_status: ficaStatus,
      },
    };

    setMerchant(merchantNewValue);
    sessionStorage.setItem(keys.merchantKey, JSON.stringify(merchantNewValue));
  };

  const saveToBackend = async (step, substep, leadOverride = null) => {
    let authTokenCurrent = authToken;
    let instanceUrlCurrent = instanceUrl;
    const leadWithOverride = leadOverride || lead;

    if (!isConnectionInitialized()) {
      const connection = await initializeConnection();
      authTokenCurrent = connection.access_token;
      instanceUrlCurrent = connection.instance_url;
    }

    if (Object.keys(terminals).length == 0) {
      const terminalsCurrent = await salesforceService.getTerminals(
        instanceUrlCurrent,
        authTokenCurrent,
      );
      if (terminalsCurrent) {
        initializeTerminals(terminalsCurrent);
        sessionStorage.setItem(keys.terminalsKey, JSON.stringify(terminalsCurrent));
        sessionStorage.setItem(keys.authTokenKey, authTokenCurrent);
        sessionStorage.setItem(keys.instanceUrlKey, instanceUrlCurrent);
      }
    }

    if (
      leadId == null &&
      step === Steps.LETS_GET_STARTED &&
      leadWithOverride.firstNames &&
      leadWithOverride.mobileNumber &&
      leadWithOverride.email
    ) {
      const newLeadId = await salesforceService.createLead(
        instanceUrlCurrent,
        authTokenCurrent,
        step,
        leadWithOverride.firstNames,
        leadWithOverride.mobileNumber,
        leadWithOverride.email,
        leadWithOverride.dealerId,
        leadWithOverride.agentId,
        leadWithOverride.salesOrg,
      );
      if (newLeadId) {
        sessionStorage.setItem(keys.leadIdKey, newLeadId);
        setLeadId(newLeadId);
      }
    }

    if (leadId == null && !isResumedSession) {
      return;
    }

    if (
      step === Steps.BUSINESS_TYPE ||
      step === Steps.LETS_GET_STARTED ||
      step === Steps.MONTHLY_CARD_TURNOVER
    ) {
      if (isLeadConverted()) {
        await salesforceService.updateMerchant(
          instanceUrlCurrent,
          authTokenCurrent,
          getMerchantRequestObject(merchant, step, substep),
        );
      } else {
        const response = await salesforceService.updateLead(
          instanceUrlCurrent,
          authTokenCurrent,
          step,
          leadId,
          getLeadRequestObject(leadWithOverride),
        );

        if (step === Steps.MONTHLY_CARD_TURNOVER && response) {
          let newMerchant = {
            ...response,
            account: {
              ...response.account,
              postal_address_same_as_business_address: false,
            },
            opportunity: {
              ...response.opportunity,
              deliveries: {},
            },
            opp_resolved: isPendingAppComplete(substep),
            opp_kill_flag: isApplicationKilled,
          };

          const sovAvailable = isSovAvailable();

          if (sovAvailable && isVodapayChannel) {
            const bizWalletProDevice = Object.values(uniqueTerminals).find(
              (terminal) => terminal.name === BizWalletName,
            );

            if (!bizWalletProDevice) {
              handleTechnicalError(
                'biz wallet pro device expected but not configured in salesforce',
              );
            }

            newMerchant.opportunity.deliveries[bizWalletProDevice.id] = {
              price: 0,
              number_of_devices: 1,
              device_id: bizWalletProDevice.id,
              device_name: bizWalletProDevice.name,
              acquiring_channel: bizWalletProDevice.acquiring_channel,
              deviceIdSalesforce: bizWalletProDevice.deviceIdSalesforce,
            };
          } else if (isVodacomBusinessChannel) {
            /* ensure vodacom business device is in local state and pre-setup values
             * from the lead object are copied into merchant object
             */
            const kwikaVodacomBusinessDevice = Object.values(uniqueTerminals).find(
              (terminal) =>
                isEqualCaseInsensitive(terminal.name, leadWithOverride.deviceType) &&
                terminal.acquiring_channel === AcquiringChannelFlags.vodacomBusiness,
            );

            const kwikaQuantity = leadWithOverride.deviceQuantity;

            if (!kwikaVodacomBusinessDevice) {
              handleTechnicalError(
                `${leadWithOverride.deviceType} device with vodacom business acquiring channel expected but not configured in salesforce`,
              );
            }

            if (!kwikaQuantity) {
              handleTechnicalError(
                `Quantity for ${leadWithOverride.deviceName} device expected in OTP response`,
              );
            }

            newMerchant.opportunity.deliveries[kwikaVodacomBusinessDevice.id] = {
              price: getKwikaRetailFee(),
              number_of_devices: kwikaQuantity,
              device_id: kwikaVodacomBusinessDevice.id,
              device_name: kwikaVodacomBusinessDevice.name,
              acquiring_channel: kwikaVodacomBusinessDevice.acquiring_channel,
              deviceIdSalesforce: kwikaVodacomBusinessDevice.deviceIdSalesforce,
            };

            newMerchant.account = {
              ...newMerchant.account,
              zip_code: leadWithOverride.postalCode,
              suburb: leadWithOverride.suburb,
              street: leadWithOverride.street,
              province: leadWithOverride.province,
              region: leadWithOverride.province,
              trading_as: leadWithOverride?.tradingAs,
              trading_name_different_to_business_name: !!leadWithOverride?.tradingAs,
            };

            newMerchant.contact = {
              ...newMerchant.contact,
              id_number: leadWithOverride.idNumber,
            };
          }

          sessionStorage.setItem(keys.merchantKey, JSON.stringify(newMerchant));
          setMerchant(newMerchant);
        }
      }
    } else if (
      step === Steps.CHOOSE_A_SOLUTION ||
      step === Steps.YOUR_BUSINESS ||
      (step === Steps.YOUR_DETAILS && substep !== Steps.UPLOAD_DOCUMENTS) ||
      step === Steps.CONFIRM_APPLICATION
    ) {
      let updatedMerchant = merchant;
      if (
        step === Steps.CONFIRM_APPLICATION &&
        getTotalDevices() === 1 &&
        merchant.account.sov_requested
      ) {
        updatedMerchant = {
          ...merchant,
          account: {
            ...merchant.account,
            settlements_method: WalletTypes.SOV,
          },
          opp_kill_flag: isApplicationKilled,
        };
      }

      if (step === Steps.CHOOSE_A_SOLUTION) {
        const getMerchantObjectEcommerceEnrichment = (merchantObject) => {
          const merchantObjectNew = { ...merchantObject };

          const { isEcomCategorySelected, isEcomTenderTypeSelected } = Object.values(
            merchantObjectNew.opportunity.deliveries,
          ).reduce(
            (result, delivery) => {
              const terminal = uniqueTerminals[delivery.device_id];
              if (
                delivery.number_of_devices > 0 &&
                (terminal.product_category === ecommerceProductCategoryName ||
                  terminal.product_category === paymentRequestProductCategoryName)
              ) {
                if (terminal.is_product_category) {
                  result.isEcomCategorySelected = true;
                } else {
                  result.isEcomTenderTypeSelected = true;
                }
              }
              return result;
            },
            {
              isEcomCategorySelected: false,
              isEcomTenderTypeSelected: false,
            },
          );

          if (isEcomCategorySelected && !isEcomTenderTypeSelected) {
            const defaultTenderTypeProduct = Object.values(uniqueTerminals).find(
              (terminal) =>
                (terminal.product_category === ecommerceProductCategoryName ||
                  terminal.product_category === paymentRequestProductCategoryName) &&
                !terminal.is_product_category,
            );

            if (!defaultTenderTypeProduct) {
              throw new Error(
                'SF Context: ecom product category configured but no tender type products found',
              );
            }

            merchantObjectNew.opportunity.deliveries[defaultTenderTypeProduct.id] = {
              acquiring_channel: defaultTenderTypeProduct.acquiring_channel,
              deviceIdSalesforce: defaultTenderTypeProduct.deviceIdSalesforce,
              device_id: defaultTenderTypeProduct.id,
              device_name: defaultTenderTypeProduct.name,
              is_product_category: false,
              number_of_devices: 1,
              price: 0,
              product_category: defaultTenderTypeProduct.product_category,
            };

            return merchantObjectNew;
          } else {
            return merchantObject;
          }
        };

        updatedMerchant = getMerchantObjectEcommerceEnrichment(updatedMerchant);
      }

      const merchantRequestObject = getMerchantRequestObject(updatedMerchant, step, substep);

      await salesforceService.updateMerchant(
        instanceUrlCurrent,
        authTokenCurrent,
        merchantRequestObject,
      );

      if (step === Steps.CHOOSE_A_SOLUTION) {
        setMerchantSaved(merchantRequestObject);
      }

      if (step === Steps.YOUR_BUSINESS && substep === Steps.BUSINESS_IDS) {
        if (deletedDocs.length > 0) {
          await removeFromCache(keys.documentsCacheKey);
          addToCache(keys.documentsCacheKey, JSON.stringify(documents));
          await salesforceService.deleteDocuments(
            instanceUrlCurrent,
            authTokenCurrent,
            deletedDocs,
          );
          setDeletedDocs([]);
        }
      }

      sessionStorage.setItem(keys.merchantKey, JSON.stringify(updatedMerchant));
    } else if (step === Steps.YOUR_DETAILS && substep === Steps.UPLOAD_DOCUMENTS) {
      let docsToUpload = [];

      if (!Environment.current.disabledFeatures.imageJoin) {
        const mergedDocs = await LegacyImageService.joinDocsById(documents.documents);
        docsToUpload = removeUnsentDocuments({ documents: mergedDocs });
      } else {
        docsToUpload = removeUnsentDocuments(documents);
      }

      if (deletedDocs.length > 0) {
        await salesforceService.deleteDocuments(instanceUrlCurrent, authTokenCurrent, deletedDocs);
        setDeletedDocs([]);
      }
      if (docsToUpload.documents.length === 0 && !merchant.isPendingApp) {
        return;
      }

      const documentBatches = splitDocumentCalls(docsToUpload);
      const uploadDoc = (doc) => {
        return new Promise((resolve) =>
          resolve(
            salesforceService.updateDocuments(
              instanceUrlCurrent,
              authTokenCurrent,
              doc,
              merchant?.account?.id,
              merchant?.contact?.id,
            ),
          ),
        );
      };
      const promises = [];
      documentBatches.forEach((doc) => promises.push(uploadDoc(doc)));
      let documentsDetail = {};
      await Promise.all(promises).then((values) => {
        values.forEach((documentResponse) =>
          documentResponse.documents.forEach(
            (document) =>
              (documentsDetail[document.content_document_id] = {
                ...document,
                document_type: getDocumentTypeCaseInsensitive(document.document_type),
              }),
          ),
        );
      });
      const updatedDocs = markDocumentsAsSent({ documents: Object.values(documentsDetail) });
      setDocuments(updatedDocs);
      removeFromCache(keys.documentsCacheKey); // Does not overwrite so clear before adding
      addToCache(keys.documentsCacheKey, JSON.stringify(documents));

      if (merchant.isPendingApp) {
        // eslint-disable-next-line @typescript-eslint/no-unused-vars
        const { documents: _discard, ...merchantObj } = getMerchantRequestObject(
          merchant,
          Steps.CONFIRM_APPLICATION,
          Steps.UPLOAD_DOCUMENTS,
        );
        await salesforceService.updateMerchant(instanceUrlCurrent, authTokenCurrent, merchantObj);
      }
    }
  };

  const saveValueIfTruthy = (saver, value) => {
    if (value) {
      saver(value);
    }
  };

  const saveToSessionStorage = (leadOverride = null) => {
    saveValueIfTruthy((value) => sessionStorage.setItem(keys.instanceUrlKey, value), instanceUrl);
    saveValueIfTruthy((value) => sessionStorage.setItem(keys.leadIdKey, value), leadId);
    saveValueIfTruthy((value) => sessionStorage.setItem(keys.customerType, value), customerType);
    saveValueIfTruthy((value) => sessionStorage.setItem(keys.priceTierKey, value), priceTier);
    saveValueIfTruthy(
      (value) => sessionStorage.setItem(keys.leadKey, JSON.stringify(value)),
      leadOverride || lead,
    );
    saveValueIfTruthy(
      (value) => sessionStorage.setItem(keys.merchantKey, JSON.stringify(value)),
      merchant,
    );
    saveValueIfTruthy((value) => {
      removeFromCache(keys.documentsCacheKey); // Does not overwrite so clear before adding
      addToCache(keys.documentsCacheKey, JSON.stringify(value));
    }, documents);
    saveValueIfTruthy(
      (value) => sessionStorage.setItem(keys.terminalsKey, JSON.stringify(value)),
      terminals,
    );
    saveValueIfTruthy(
      (value) => sessionStorage.setItem(keys.isResumedSessionKey, JSON.stringify(value)),
      isResumedSession,
    );
    saveValueIfTruthy(
      (value) => sessionStorage.setItem(keys.deviceDeliveryDetailsKey, JSON.stringify(value)),
      deviceDeliveryDetails,
    );
    saveValueIfTruthy(
      (value) => sessionStorage.setItem(keys.opportunityCardFeesKey, JSON.stringify(value)),
      opportunityCardFees,
    );
    saveValueIfTruthy(
      (value) => sessionStorage.setItem(keys.solutionType, JSON.stringify(value)),
      solutionType,
    );
    saveValueIfTruthy(
      (value) => sessionStorage.setItem(keys.ficaPollingStartTime, JSON.stringify(value)),
      ficaPollingStartTime,
    );
  };

  const save = async (step, substep, suppressValidation = false, leadOverride = null) => {
    saveToSessionStorage(leadOverride);

    if (!suppressValidation) {
      await saveToBackend(step, substep, leadOverride);
    }
  };

  const loadValueIfTruthy = (loader, key) => {
    const value = sessionStorage.getItem(key);
    if (value) {
      loader(value);
    }
  };

  const loadFromSessionStorage = () => {
    loadValueIfTruthy(setInstanceUrl, keys.instanceUrlKey);
    loadValueIfTruthy(setLeadId, keys.leadIdKey);
    loadValueIfTruthy(setCustomerType, keys.customerType);
    loadValueIfTruthy(setPriceTier, keys.priceTierKey);
    loadValueIfTruthy((value) => setLead(JSON.parse(value)), keys.leadKey);
    loadValueIfTruthy((value) => setMerchant(JSON.parse(value)), keys.merchantKey);
    loadValueIfTruthy((value) => initializeTerminals(JSON.parse(value)), keys.terminalsKey);
    loadValueIfTruthy((value) => setIsResumedSession(JSON.parse(value)), keys.isResumedSessionKey);
    loadValueIfTruthy(
      (value) => setDeviceDeliveryDetails(JSON.parse(value)),
      keys.deviceDeliveryDetailsKey,
    );
    loadValueIfTruthy(
      (value) => setOpportunityCardFees(JSON.parse(value)),
      keys.opportunityCardFeesKey,
    );
    loadValueIfTruthy((value) => setSolutionType(JSON.parse(value)), keys.solutionType);
    loadValueIfTruthy(
      (value) => setFicaPollingStartTime(JSON.parse(value)),
      keys.ficaPollingStartTime,
    );
    setIsLoadingFromSessionStorage(false);
  };

  useEffect(() => {
    initialiseCacheIfNonExistent();
    loadFromSessionStorage();
  }, []);

  // Utility setters and getters

  const isProductCategory = (deviceId) => uniqueTerminals[deviceId].is_product_category;

  const setAllDeliveriesInCategoryToZero = (productCategory) => {
    Object.values(merchant.opportunity.deliveries)
      .filter((delivery) => delivery.product_category === productCategory)
      .forEach((delivery) => setDeviceOrderQuantity(delivery.device_id, 0, 0));
  };

  const setDeviceOrderQuantity = (deviceId, quantity, costPerUnit) => {
    let newState = { ...merchant };
    if (newState.opportunity.deliveries[deviceId]) {
      if (quantity === 0) {
        delete newState.opportunity.deliveries[deviceId];
      } else {
        newState.opportunity.deliveries[deviceId].number_of_devices = quantity;
        newState.opportunity.deliveries[deviceId].price = costPerUnit;
      }
    }
    setMerchant(newState);
  };

  const getDeviceOrderQuantity = (deviceId) => {
    return merchant?.opportunity?.deliveries[deviceId]?.number_of_devices || 0;
  };

  const getDeviceOrder = (deviceId) => {
    return merchant?.opportunity?.deliveries[deviceId];
  };

  const setDeviceOrder = (deviceId, quantity, costPerUnit) => {
    let newState = { ...merchant };
    let quantityInt = parseInt(quantity);

    if (isNaN(quantityInt)) {
      quantityInt = 0;
    }

    if (newState?.opportunity?.deliveries[deviceId]) {
      if (quantityInt === 0) {
        delete newState.opportunity.deliveries[deviceId];
      } else {
        newState.opportunity.deliveries[deviceId].number_of_devices = quantityInt;
        newState.opportunity.deliveries[deviceId].price = costPerUnit;
      }
    } else {
      const terminalInfo = uniqueTerminals[deviceId];
      newState.opportunity.deliveries[deviceId] = {
        number_of_devices: quantity,
        device_id: deviceId,
        device_name: terminalInfo.name,
        price: costPerUnit,
        acquiring_channel: terminalInfo.acquiring_channel,
        deviceIdSalesforce: terminalInfo.deviceIdSalesforce,
        product_category: terminalInfo.product_category,
        is_product_category: terminalInfo.is_product_category,
      };
    }
    setMerchant(newState);
  };

  const isUseFixedCardFees = () =>
    !merchant?.opportunity?.deliveries ||
    Object.keys(merchant?.opportunity?.deliveries).length === 0
      ? false
      : Object.values(merchant?.opportunity?.deliveries || {}).every((deliveryInfo) => {
          const terminal = uniqueTerminals[deliveryInfo.device_id];
          const quantityOrdered = deliveryInfo.number_of_devices;
          const kwikaName = Environment.current?.kwikaName || 'VodaPay Kwika';
          const isKwika = deliveryInfo.device_name === kwikaName;
          const isVodapayPaymentGateway =
            deliveryInfo.device_name === DEVICE_NAMES.VODAPAY_ECOM_GATEWAY;
          const isVodaPayMax = deliveryInfo.device_name === DEVICE_NAMES.VODAPAY_MAX;
          const isTapOnPhone = deliveryInfo.device_name === DEVICE_NAMES.TAP_ON_PHONE;

          if (quantityOrdered > 0) {
            if (isTapOnPhone && (isVodaPayMax || isVodapayPaymentGateway)) {
              return false;
            }
            if (isTapOnPhone) {
              return true;
            }
            return isKwika || !terminal?.card_present;
          }
          return true;
        });

  const isUseFixedCardFeesForDisplay = (terminalId) => {
    const terminal = uniqueTerminals[terminalId];

    if ([kwikaName, chopChopName].includes(terminal.name)) {
      const quantity = getDeviceOrderQuantity(terminalId);
      const maxDeviceIds = getAllDeviceIdsByName(maxName, uniqueTerminals);
      const isMaxQuantityNonZero =
        maxDeviceIds.length === 0
          ? false
          : maxDeviceIds.some((deviceId) => getDeviceOrderQuantity(deviceId) > 0);

      const paymentGatewayIds = getAllDeviceIdsByName(
        DEVICE_NAMES.VODAPAY_ECOM_GATEWAY,
        uniqueTerminals,
      );
      const isPaymentGatewayQuantityNonZero =
        paymentGatewayIds.length === 0
          ? false
          : paymentGatewayIds.some((deviceId) => getDeviceOrderQuantity(deviceId) > 0);
      return quantity === 0 || (!isMaxQuantityNonZero && !isPaymentGatewayQuantityNonZero);
    } else {
      return false;
    }
  };

  function isPendingAppComplete(subStepName) {
    if (!merchant.isPendingApp || !merchant.pageUpdates) {
      return false;
    }

    const pendingAppDocSize = merchant.pageUpdates.length - 1;
    const isOnLastPendingAppStep =
      merchant.pageUpdates[pendingAppDocSize].subStepName === subStepName;

    return isOnLastPendingAppStep;
  }

  return (
    <SalesforceContext.Provider
      value={{
        authToken,
        setAuthToken,
        instanceUrl,
        setInstanceUrl,
        lead,
        setLead,
        merchant,
        setMerchant,
        documents,
        setDocuments,
        terminals,
        initializeTerminals,
        save,
        getDeviceOrder,
        setDeviceOrder,
        getDeviceOrderQuantity,
        setDeviceOrderQuantity,
        deviceDeliveryDetails,
        setDeviceDeliveryDetails,
        priceTier,
        setPriceTier,
        updateOpportunityCardFees,
        resumeSessionRequestOTP,
        resumeSessionSubmitOTP,
        otpEmailAddress,
        isResumedSession,
        getDeviceDeliveryQuantity,
        ensureDeliveryQuantityMatchesOrderQuantity,
        clearCache,
        solutionType,
        setSolutionType,
        isLoadingFromSessionStorage,
        uniqueTerminals,
        hasBuyOnlineDevice,
        paymentGatewayAmount,
        setPaymentGatewayAmount,
        leadId,
        setLeadId,
        customerType,
        setCustomerType,
        dealerId,
        setDealerId,
        refreshFicaStatus,
        getFicaStatus,
        initFicaPollingStartTime,
        applicationComplete,
        setApplicationComplete,
        deletedDocs,
        setDeletedDocs,
        setDocumentsWithDeletionHandling,
        otpRetriesExceeded,
        setOtpRetriesExceeded,
        isVodapayChannel,
        getSettlementsMethod,
        journeyType,
        setJourneyType,
        noBuyInStoreDevicesExist,
        isHideBuyOnlineDevices,
        isUseFixedCardFees,
        isIncompleteSOVJourney,
        setIsIncompleteSOVJourney,
        isFieldLocked,
        isVodacomBusinessChannel,
        isUseFixedCardFeesForDisplay,
        isLeadConvertedAsync,
        getTotalDevices,
        revertApplicatonToSavedState,
        clearApplicationState,
        setBankingDetailsPassBav,
        getBankingDetailsPassBav,
        setAllDeliveriesInCategoryToZero,
        isProductCategory,
        isApplicationKilled,
        setApplicationKilled,
      }}
    >
      {children}
    </SalesforceContext.Provider>
  );
}

SalesforceProvider.propTypes = { children: PropTypes.any };
